<script setup lang="ts">
import { FavoriteEdit, PopoverButtons, ToolButton } from '@/components';
import { FabricEditor, DecalEditor, ResolutionToggle } from '.';
import { useFabricStore } from '@/store/fabric.ts';
import { useRoute, useRouter } from 'vue-router';
import { base64ToFile, joinUrl } from '@repo/utils';
import { showToast } from 'vant';
import { useStyle3d } from '@repo/style3d';
import { $t } from '@/locales';
import { SvgEllipsisHIcon, SvgStarOutlineIcon } from '@repo/icons';
import { computed, ref } from 'vue';
import { StyleOrder } from '@/views/fabric/components/style-order';
import { getDashscopeInstantKey } from '@/api';
import { getAppConfig } from '@/utils';
import axios, { type AxiosResponse } from 'axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { Material,MetadataProperty,Relation,Shape,FabricShareInfo } from '@repo/style3d';
import { useRelationStore } from '@repo/stores';
import { useParamStore } from '@/store/param.ts';

const paramStore = useParamStore();
const relationStore = useRelationStore();
const fabricStore = useFabricStore();

const router = useRouter();
const route = useRoute();

const enterpriseId = computed(() => fabricStore.fabricInfo?.enterpriseId || '');

const style3d = useStyle3d();
const onClickTryon = async () => {
  if (route.params?.id === 'fabric_hanger') {
    showToast($t('message.select-sco'));
    return;
  }

  if (enterpriseId.value === '') {
    showToast('企业id为空');
    return;
  }

  const screenshot = await style3d.getScreenshot();
  const fabricId = fabricStore?.fabricInfo?.fabricId || '';
  const originUrl = fabricStore?.fabricInfo?.originUrl || '';

  const file = base64ToFile(screenshot, 'screenshot.png');
  const key = await getDashscopeInstantKey(enterpriseId.value, file, 'screenshot.png');

  localStorage.setItem('lastTryonImage', JSON.stringify({
    key,
    screenshot,
  }));

  await router.push({
    name: 'tryon',
    query: {
      fabricId,
      originUrl: btoa(originUrl)
    }
  });
};

const onClickTailor = async () => {
  await router.replace({
    name: 'tailor',
    query: {}
  });
};

const showEditor = ref(false);
const onClickEditor = () => {
  showEditor.value = true;
};

const showDecal = ref(false);
const onClickDecal = () => {
  showDecal.value = true;
};

const showOrder = ref(false);
const onClickOrder = async () => {
  showOrder.value = true;
};

const generateQRCode = async (text: string, size: number = 200): Promise<string> => {
  try {
    return await QRCode.toDataURL(text, {
      width: size,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (error) {
    console.error('生成二维码失败:', error);
    throw error;
  }
};

// 将中文文本转换为图片
const textToImage = async (text: string, fontSize: number = 12, color: string = '#000000'): Promise<string> => {
  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.left = '-9999px';
  div.style.top = '-9999px';
  div.style.fontSize = `${fontSize}px`;
  div.style.color = color;
  div.style.fontFamily = 'Arial, sans-serif';
  div.style.padding = '5px';
  div.style.whiteSpace = 'nowrap';
  div.textContent = text;
  
  document.body.appendChild(div);
  
  try {
    const canvas = await html2canvas(div, {
      backgroundColor: null,
      scale: 2
    });
    const dataUrl = canvas.toDataURL('image/png');
    document.body.removeChild(div);
    return dataUrl;
  } catch (error) {
    document.body.removeChild(div);
    throw error;
  }
};

// 裁剪图片周围的空白区域
const cropImageWhitespace = async (imageDataUrl: string, padding: number = 20): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('无法创建canvas上下文'));
        return;
      }
      
      // 创建临时canvas来分析图片
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) {
        reject(new Error('无法创建临时canvas上下文'));
        return;
      }
      
      tempCtx.drawImage(img, 0, 0);
      const imageData = tempCtx.getImageData(0, 0, img.width, img.height);
      const pixels = imageData.data;
      
      // 查找非空白区域的边界
      let minX = img.width;
      let minY = img.height;
      let maxX = 0;
      let maxY = 0;
      
      // 扫描所有像素，找到非白色/透明的像素
      for (let y = 0; y < img.height; y++) {
        for (let x = 0; x < img.width; x++) {
          const index = (y * img.width + x) * 4;
          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const a = pixels[index + 3];
          
          // 如果不是纯白色或透明，则认为是内容
          if (a > 10 && (r < 250 || g < 250 || b < 250)) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      
      // 如果没有找到内容，返回原图
      if (minX >= maxX || minY >= maxY) {
        resolve(imageDataUrl);
        return;
      }
      
      // 添加padding，但不超出原图边界
      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(img.width - 1, maxX + padding);
      maxY = Math.min(img.height - 1, maxY + padding);
      
      // 计算裁剪后的尺寸
      const cropWidth = maxX - minX + 1;
      const cropHeight = maxY - minY + 1;
      
      // 创建裁剪后的canvas
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      
      // 绘制裁剪后的图片
      ctx.drawImage(img, minX, minY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.onerror = () => {
      reject(new Error('图片加载失败'));
    };
    
    img.src = imageDataUrl;
  });
};

// 获取面料信息列表（最多3个）
const getFabricInfoList = async (): Promise<Array<{ name: string; thumb: string; index: number }>> => {
  try {
    const fabricList: Array<{ name: string; thumb: string; index: number }> = [];
    // 获取当前面料的缩略图
    const currentFabricThumb = fabricStore.fabricInfo?.thumb || '';
    const currentFabricName = fabricStore.fabricInfo?.info?.name || '未命名面料';
    
    const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
    const projectRootPath = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
    const fabridRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

    const scoId = route.query.scoId as string;
    let shapeList:Shape[] = [];
    let propertyFabrics: MetadataProperty[]|undefined = undefined;

    if (paramStore.resourceParam.scoResource.type === 'style3d'){
      const { id } = route.params as { [key: string]: string };
      if (!id){
        return [];
      }

      let name : string = "";
      const thumb : string = joinUrl(fabridRootPath, id, 'fabric', 'thumbnail.jpg')
      try {
        let infoJson : AxiosResponse<FabricShareInfo> = await axios.get<FabricShareInfo>(joinUrl(fabridRootPath, id, 'info.json'));
        name = infoJson.data?.metaData?.info?.name || name;
      } catch (error) {
        console.error(`获取面料${i}的info.json失败:`, error);
      }
      
      fabricList.push({
        name: name,
        thumb: thumb,
        index: 0
      });
    } else {
       // 获取relation.json中的shape列表和metadata.propertyFabrics
      if (scoId && scoId !== 'fabric_hanger') {
        try {
          const jsonResult:AxiosResponse<Relation> = await axios.get(joinUrl(projectRootPath, scoId, 'relation.json'));
          shapeList = jsonResult.data.shape;
          propertyFabrics = jsonResult.data.metadata?.propertyFabrics;
        } catch (error) {
          console.error('获取款式名称失败:', error);
        }
      }

      if (!shapeList || shapeList.length == 0){
        return [];
      }

      let checkMaterialIndexExist :(i:number)=>boolean = (i) => {
        let findShape:Shape | undefined = shapeList.find(item=>item.materialIndex == i);
        if (!findShape)
          return false; // 改用continue，继续检查下一个面料
        return true;
      };

      for (let i = 0; i < 3; ++i){
        if (!checkMaterialIndexExist(i))
          continue; // 改用continue，继续检查下一个面料

        let thumb = currentFabricThumb;
        let name = currentFabricName;

        let findInStore = relationStore.shapeList.find(item=>item.materialIndex == i && item.type == 'main');
        if (!findInStore)
          continue; // 改用continue，继续检查下一个面料
        let id = findInStore?.fabric?.id;
        if (!id)
          continue; // 改用continue，继续检查下一个面料
        let fileName = findInStore?.name;
        if (!fileName)
          continue; // 改用continue，继续检查下一个面料
        if (fileName.startsWith('http')){
          thumb = fileName;
        }
        else{
          thumb = joinUrl(fabridRootPath, id, 'fabric', fileName);
        }

        name = propertyFabrics?.[i]?.data?.find(item => item.key === 'fabricName')?.value || '未命名面料';

        try {
          let infoJson : AxiosResponse<FabricShareInfo> = await axios.get<FabricShareInfo>(joinUrl(fabridRootPath, id, 'info.json'));
          name = infoJson.data?.metaData?.info?.name || name;
        } catch (error) {
          console.error(`获取面料${i}的info.json失败:`, error);
        }
        
        fabricList.push({
          name: name,
          thumb: thumb,
          index: i + 1
        });
      }
    }
    return fabricList;
  } catch (error) {
    console.error('获取面料列表失败:', error);
    return [];
  }
};

// 保存为PDF
const onClickSavePDF = async () => {
  try {
    showToast('正在生成PDF...');
    
    // 获取截图
    const screenshot = await style3d.getScreenshot();
    
    // 裁剪截图周围的空白区域（保留20像素边距）
    const croppedScreenshot = await cropImageWhitespace(screenshot, 20);
    
    // 获取当前URL
    const currentUrl = window.location.href;
    
    // 获取款式名称
    let styleName = '未命名';
    const scoId = route.query.scoId as string;
    
    if (scoId && scoId !== 'fabric_hanger') {
        styleName = paramStore.resourceParam.scoResource.name || '未命名款式';
    }
    
    // 生成二维码
    const qrCodeDataUrl = await generateQRCode(currentUrl, 400);
    
    // 获取面料信息列表
    const fabricList = await getFabricInfoList();
    
    // 生成中文文本图片（统一字体大小和高度）
    const fontSize = 14; // 统一字体大小
    const textHeight = 8; // 统一文字高度（mm）
    
    const styleNameImg = await textToImage(styleName, fontSize);
    const qrTextImg = await textToImage('扫码查看款式3D', fontSize);
    
    // 创建PDF
    const pdf = new jsPDF({
      orientation: 'landscape', // 横向
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    
    // 加载截图
    const screenshotImg = new Image();
    screenshotImg.src = croppedScreenshot;
    await new Promise((resolve, reject) => {
      screenshotImg.onload = resolve;
      screenshotImg.onerror = reject;
    });
    
    // ========== 左侧区域 ==========
    // 款式图始终占一半宽度
    const leftWidth = pageWidth * 0.5;
    const leftHeight = pageHeight - margin * 2; // 左侧可用高度
    
    // 计算图片和名称的总高度
    let modelWidth = leftWidth - 10;
    let modelHeight = modelWidth * (screenshotImg.height / screenshotImg.width);
    const nameSpacing = 5; // 图片和名称之间的间距
    let totalContentHeight = modelHeight + nameSpacing + textHeight;
    
    // 检查是否超出画布高度，如果超出则等比例缩小
    if (totalContentHeight > leftHeight) {
      const scale = leftHeight / totalContentHeight;
      modelWidth = modelWidth * scale;
      modelHeight = modelHeight * scale;
      totalContentHeight = modelHeight + nameSpacing + textHeight;
    }
    
    // 垂直居中：计算起始Y位置
    let leftY = margin + (leftHeight - totalContentHeight) / 2;
    
    // 1. 3D模型图片 - 水平居中
    const modelX = margin + (leftWidth - modelWidth) / 2;
    
    // 绘制模型图片
    pdf.addImage(croppedScreenshot, 'PNG', modelX, leftY, modelWidth, modelHeight);
    leftY += modelHeight + nameSpacing;
    
    // 2. 款式名称 - 水平居中
    const styleNameImgObj = new Image();
    styleNameImgObj.src = styleNameImg;
    await new Promise((resolve) => {
      styleNameImgObj.onload = resolve;
      styleNameImgObj.onerror = resolve;
    });
    // 使用固定高度，宽度按比例计算
    const styleNameImgHeight = textHeight;
    const styleNameImgWidth = (styleNameImgObj.width / styleNameImgObj.height) * styleNameImgHeight;
    const nameX = margin + (leftWidth - styleNameImgWidth) / 2;
    pdf.addImage(styleNameImg, 'PNG', nameX, leftY, styleNameImgWidth, styleNameImgHeight);
    
    // ========== 右侧区域 ==========
    const rightX = margin + leftWidth + 15; // 增加左右间距
    const rightWidth = pageWidth - rightX - margin;
    const rightHeight = pageHeight - margin * 2;
    
    // 右侧面料布局
    if (fabricList.length > 0) {
      // 如果面料数<=1，使用更大的尺寸（左右各占一半）
      if (fabricList.length <= 1) {
        // 上下布局：上面是二维码，下面是面料
        const itemSize = Math.min(rightWidth * 0.7, rightHeight * 0.4); // 单个元素尺寸
        const itemSpacing = 15; // 二维码和面料之间的间距
        const nameHeight = 12;
        
        // 计算总高度
        const totalHeight = itemSize + nameHeight + itemSpacing + itemSize + nameHeight;
        const startY = margin + (rightHeight - totalHeight) / 2;
        
        // 1. 二维码（上方）
        const qrX = rightX + (rightWidth - itemSize) / 2;
        const qrY = startY;
        
        // 绘制二维码
        pdf.addImage(qrCodeDataUrl, 'PNG', qrX + 1, qrY + 1, itemSize - 2, itemSize - 2);
        pdf.link(qrX, qrY, itemSize, itemSize, { url: currentUrl });
        
        // 在二维码下方显示"扫码查看款式3D"
        const qrTextImgObj = new Image();
        qrTextImgObj.src = qrTextImg;
        await new Promise((resolve) => {
          qrTextImgObj.onload = resolve;
          qrTextImgObj.onerror = resolve;
        });
        const qrTextImgHeight = textHeight;
        const qrTextImgWidth = (qrTextImgObj.width / qrTextImgObj.height) * qrTextImgHeight;
        const qrTextX = qrX + (itemSize - qrTextImgWidth) / 2;
        const qrTextY = qrY + itemSize + 6;
        pdf.addImage(qrTextImg, 'PNG', qrTextX, qrTextY, qrTextImgWidth, qrTextImgHeight);
        
        // 2. 面料（下方）
        const fabric = fabricList[0];
        const fabricX = rightX + (rightWidth - itemSize) / 2;
        const fabricY = qrY + itemSize + nameHeight + itemSpacing;
        
        // 绘制面料图片
        if (fabric.thumb) {
          try {
            const fabricImg = new Image();
            fabricImg.crossOrigin = 'anonymous';
            fabricImg.src = fabric.thumb;
            await new Promise((resolve) => {
              fabricImg.onload = resolve;
              fabricImg.onerror = resolve;
            });
            pdf.addImage(fabric.thumb, 'PNG', fabricX + 2, fabricY + 2, itemSize - 4, itemSize - 4);
          } catch (error) {
            console.error('加载面料图片失败:', error);
          }
        }
        
        // 在面料图片下方显示面料名称
        const fabricNameImg = await textToImage(fabric.name, fontSize);
        const fabricNameImgObj = new Image();
        fabricNameImgObj.src = fabricNameImg;
        await new Promise((resolve) => {
          fabricNameImgObj.onload = resolve;
          fabricNameImgObj.onerror = resolve;
        });
        const fabricNameImgHeight = textHeight;
        const fabricNameImgWidth = (fabricNameImgObj.width / fabricNameImgObj.height) * fabricNameImgHeight;
        const maxFabricNameWidth = itemSize * 0.95;
        const finalFabricNameWidth = Math.min(fabricNameImgWidth, maxFabricNameWidth);
        const finalFabricNameHeight = (fabricNameImgHeight * finalFabricNameWidth) / fabricNameImgWidth;
        const fabricNameX = fabricX + (itemSize - finalFabricNameWidth) / 2;
        const fabricNameY = fabricY + itemSize + 6;
        pdf.addImage(fabricNameImg, 'PNG', fabricNameX, fabricNameY, finalFabricNameWidth, finalFabricNameHeight);
      } else {
        // 多个面料时使用2x2网格布局
        const fabricSize = Math.min(rightWidth / 2.6, rightHeight / 3.2);
        const horizontalSpacing = 15;
        const verticalSpacing = 25;
        const nameHeight = 12;
        
        // 计算起始位置（居中）
        const gridWidth = fabricSize * 2 + horizontalSpacing;
        const gridHeight = fabricSize * 2 + verticalSpacing + nameHeight * 2;
        const startX = rightX + (rightWidth - gridWidth) / 2;
        const startY = margin + (rightHeight - gridHeight) / 2;
        
        // 位置映射：根据图片，面料显示顺序为
        // 二维码(左上) | 3(右上)
        // 1(左下)      | 2(右下)
        const positions = [
          { x: startX, y: startY + fabricSize + verticalSpacing + nameHeight, label: '1' }, // 左下
          { x: startX + fabricSize + horizontalSpacing, y: startY + fabricSize + verticalSpacing + nameHeight, label: '2' }, // 右下
          { x: startX + fabricSize + horizontalSpacing, y: startY, label: '3' }, // 右上
        ];
        
        // 左上角：二维码
        const qrSizeRight = fabricSize;
        const qrXRight = startX;
        const qrYRight = startY;
        
        // 绘制二维码
        pdf.addImage(qrCodeDataUrl, 'PNG', qrXRight + 1, qrYRight + 1, qrSizeRight - 2, qrSizeRight - 2);
        pdf.link(qrXRight, qrYRight, qrSizeRight, qrSizeRight, { url: currentUrl });
        
        // 在二维码下方显示"扫码查看款式3D"
        const qrTextImgObj = new Image();
        qrTextImgObj.src = qrTextImg;
        await new Promise((resolve) => {
          qrTextImgObj.onload = resolve;
          qrTextImgObj.onerror = resolve;
        });
        const qrTextImgHeight = textHeight;
        const qrTextImgWidth = (qrTextImgObj.width / qrTextImgObj.height) * qrTextImgHeight;
        const qrTextX = qrXRight + (qrSizeRight - qrTextImgWidth) / 2;
        const qrTextY = qrYRight + qrSizeRight + 6;
        pdf.addImage(qrTextImg, 'PNG', qrTextX, qrTextY, qrTextImgWidth, qrTextImgHeight);
        
        // 绘制面料（最多3个）
        for (let i = 0; i < Math.min(fabricList.length, 3); i++) {
          const fabric = fabricList[i];
          const pos = positions[i];
          
          // 绘制面料图片
          if (fabric.thumb) {
            try {
              const fabricImg = new Image();
              fabricImg.crossOrigin = 'anonymous';
              fabricImg.src = fabric.thumb;
              await new Promise((resolve) => {
                fabricImg.onload = resolve;
                fabricImg.onerror = resolve;
              });
              pdf.addImage(fabric.thumb, 'PNG', pos.x + 2, pos.y + 2, fabricSize - 4, fabricSize - 4);
            } catch (error) {
              console.error('加载面料图片失败:', error);
            }
          }
          
          // 在面料图片下方显示面料名称（统一高度）
          const fabricNameImg = await textToImage(fabric.name, fontSize);
          const fabricNameImgObj = new Image();
          fabricNameImgObj.src = fabricNameImg;
          await new Promise((resolve) => {
            fabricNameImgObj.onload = resolve;
            fabricNameImgObj.onerror = resolve;
          });
          const fabricNameImgHeight = textHeight;
          const fabricNameImgWidth = (fabricNameImgObj.width / fabricNameImgObj.height) * fabricNameImgHeight;
          const maxFabricNameWidth = fabricSize * 0.95;
          const finalFabricNameWidth = Math.min(fabricNameImgWidth, maxFabricNameWidth);
          const finalFabricNameHeight = (fabricNameImgHeight * finalFabricNameWidth) / fabricNameImgWidth;
          const fabricNameX = pos.x + (fabricSize - finalFabricNameWidth) / 2;
          const fabricNameY = pos.y + fabricSize + 6;
          pdf.addImage(fabricNameImg, 'PNG', fabricNameX, fabricNameY, finalFabricNameWidth, finalFabricNameHeight);
        }
      }
    }
    
    // 保存PDF
    pdf.save(`${styleName}-${Date.now()}.pdf`);
    showToast('保存成功');
  } catch (error) {
    console.error('保存PDF失败:', error);
    showToast('保存失败');
  }
};

const onClickSave = async () => {
  try {
    showToast('正在生成图片...');
    
    // 获取截图
    const screenshot = await style3d.getScreenshot();
    
    // 获取当前URL
    const currentUrl = window.location.href;
    
    // 获取名称：如果有款式则用款式名称，否则用面料名称
    let displayName = '未命名';
    const scoId = route.query.scoId as string;
    
    // 判断是否有款式（不是默认的fabric_hanger）
    if (scoId && scoId !== 'fabric_hanger') {
      // 有款式，获取款式名称
      displayName = paramStore.resourceParam.scoResource.name || '未命名款式';
    } else {
      // 只有面料，使用面料名称
      displayName = fabricStore.fabricInfo?.info?.name || '未命名面料';
    }
    
    // 生成二维码
    const qrCodeDataUrl = await generateQRCode(currentUrl, 200);
    
    // 创建组合图片
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('无法创建canvas上下文');
    }
    
    // 加载截图
    const screenshotImg = new Image();
    screenshotImg.src = screenshot;
    
    await new Promise((resolve, reject) => {
      screenshotImg.onload = resolve;
      screenshotImg.onerror = reject;
    });
    
    // 加载二维码
    const qrImg = new Image();
    qrImg.src = qrCodeDataUrl;
    
    await new Promise((resolve, reject) => {
      qrImg.onload = resolve;
      qrImg.onerror = reject;
    });
    
    // 计算画布尺寸
    const padding = 40;
    const qrSize = 200;
    const textHeight = 60;
    const canvasWidth = Math.max(screenshotImg.width, qrSize + padding * 2);
    const canvasHeight = screenshotImg.height + padding + qrSize + padding + textHeight + padding;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    // 填充白色背景
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // 绘制截图（居中）
    const screenshotX = (canvasWidth - screenshotImg.width) / 2;
    ctx.drawImage(screenshotImg, screenshotX, 0, screenshotImg.width, screenshotImg.height);
    
    // 绘制二维码（居中）
    const qrX = (canvasWidth - qrSize) / 2;
    const qrY = screenshotImg.height + padding;
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
    
    // 绘制名称（居中）
    const textY = qrY + qrSize + padding;
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 24px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(displayName, canvasWidth / 2, textY);
    
    // 创建下载链接
    const finalImage = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = finalImage;
    link.download = `${displayName}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('保存成功');
  } catch (error) {
    console.error('保存失败:', error);
    showToast('保存失败');
  }
};
</script>

<template>
  <PopoverButtons dark placement="left">
    <template #reference>
      <ToolButton id="more-button">
        <SvgEllipsisHIcon />
      </ToolButton>
    </template>
    <ResolutionToggle />
    <div v-show="false">
      <ToolButton>
        {{ $t('common.edit') }}
      </ToolButton>
    </div>
    <div>
      <ToolButton @click="onClickTryon">
        {{ $t('toolbar.outfit') }}
      </ToolButton>
    </div>
    <!-- 暂时隐藏 -->
    <div v-if="false">
      <ToolButton @click="onClickTailor">
        {{ $t('toolbar.tailor') }}
      </ToolButton>
    </div>
    <div>
      <ToolButton @click="onClickEditor">
        换色
      </ToolButton>
      <FabricEditor v-model:show="showEditor" />
    </div>
    <div v-if="false">
      <ToolButton @click="onClickDecal">
        图案
      </ToolButton>
      <DecalEditor v-model:show="showDecal" />
    </div>
    <div>
      <ToolButton @click="onClickOrder" :disabled="!enterpriseId">
        下单
      </ToolButton>
      <StyleOrder v-model:show="showOrder" :enterprise-id="enterpriseId" />
    </div>
    <div>
      <PopoverButtons dark placement="left">
        <template #reference>
          <ToolButton>
            保存
          </ToolButton>
        </template>
        <div>
          <ToolButton @click="onClickSave">
            保存为BMP
          </ToolButton>
        </div>
        <div>
          <ToolButton @click="onClickSavePDF">
            保存为PDF
          </ToolButton>
        </div>
      </PopoverButtons>
    </div>
  </PopoverButtons>
  <FavoriteEdit v-bind="fabricStore.fabricInfo">
    <ToolButton>
      <SvgStarOutlineIcon style="width: 20px;height:20px;" />
    </ToolButton>
  </FavoriteEdit>
</template>

<style module>

</style>
